import React, { useState, useMemo, useEffect } from 'react';
import { compose } from 'redux';
import { withProfile } from 'modules/v2/containers';
import { withAuth } from 'modules/auth/containers';
import DashboardTemplate from 'modules/v2/templates/DashboardTemplate';
import RelativeLoading from 'modules/v2/common/AtomicDesign/RelativeLoading';
import { isEmpty, isEqual, map, uniqWith } from 'lodash';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { ConfirmModal, PreviewModal } from 'modules/v2/common/components';
import { useHistory, useLocation } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';

// Page Components
import { DesignHuddleProvider, useDesignHuddle } from 'modules/v2/pages/TemplatesBuilder/DesignHuddleContext';
import SocialMediaTemplates from 'modules/v2/pages/TemplatesBuilder/page-components/SocialMediaTemplates';
import AdsTemplates from 'modules/v2/pages/TemplatesBuilder/page-components/AdsTemplates';
import DefaultTemplates from 'modules/v2/pages/TemplatesBuilder/page-components/DefaultTemplates';
import EmailTemplates from 'modules/v2/pages/TemplatesBuilder/page-components/EmailTemplates';
import { getCategoryFromUrl } from 'modules/v2/pages/TemplatesBuilder/utils';

import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';

import { Loader } from 'modules/common/components';

const isSocialMediaRoute = () => window.location.pathname.includes('/social-media-templates');
const isAdsRoute = () => window.location.pathname.includes('/ads');
const isWelcomeBundleRoute = () => window.location.pathname.includes('/welcome-bundle');
const isEmailBannerRoute = () => window.location.pathname.includes('/email-banner');
const isInstantTrustPackageRoute = () => window.location.pathname.includes('/instant-trust-package');

const TemplatesContent = () => {
  const location = useLocation();

  const {
    dhTemplates,
    isLoadingTemplates,
    dhProjects,
    searchCategories,
    handleSelectPrimaryCategory,
    handleSelectSecondaryCategory,
    selectedCategories,
    setSelectedCategories,
    currentPage,
    totalPages,
    totalTemplates,
    handlePageChange,
    toggleAllSecondaryCategories,
    resetSelectedFilters,
    userCustomizationsHash,
  } = useDesignHuddle();

  const history = useHistory();
  const [currentCategory, setCurrentCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [checkedCategories, setCheckedCategories] = useState({});
  const [selectedSecondaryFilters, setSelectedSecondaryFilters] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);

  // const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColor, setSelectedColor] = useState('blue');
  
  const [navbarCategories, setNavbarCategories] = useState(null);
  const [selectedSecondaryCategory, setSelectedSecondaryCategory] = useState('all');

  const [isCreatingProject, setIsCreatingProject] = useState(false);

  const isAtColorPage = () => {
    return (
      isAdsRoute()
      || isWelcomeBundleRoute()
    );
  };

  const filteredTemplates = useMemo(() => {
    return dhTemplates.filter(template => {
      const matchesSearch = !searchQuery ||
        template.template_title?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesColor = !selectedColor ||
        (!isAtColorPage()) ||
        template.template_title?.toLowerCase().includes(selectedColor.toLowerCase());

      return matchesSearch && matchesColor;
    });
  }, [dhTemplates, searchQuery, selectedColor, isAtColorPage]);

  const existingProjectsWith = (templateId) => {
    if (!dhProjects || dhProjects.length === 0) return [];

    return dhProjects.filter(project =>
      String(project?.templateId) === String(templateId)
    );
  };

  const createProject = (template, postCreationCallback) => {
    window.DSHDLib.createProject(
      {
        template_id: template.template_id,
        customizations_hash: userCustomizationsHash,
      },
      (err, data) => {
        if (err) {
          console.error('Template Builder Error:', err);
        } else {
          window.DSHDLib.getProject(data.project_id, (_err, project) => {
            postCreationCallback(project);
          });
        }
      }
    );
  };

  const foundSecondaryCategory = () => {
    if (!currentCategory?.id) return [];
    const foundOne = searchCategories?.secondary?.find((desiredCategory) => desiredCategory?.id === currentCategory?.id);

    if (!foundOne) return [];

    // if (isSocialMediaRoute) {
    //   const reversed = foundOne?.items?.reverse();

    //   return reversed;
    // }

    return foundOne?.items;
  };

  const handleCustomize = async (template, skipCheck = false) => {
    const existingProjects = existingProjectsWith(template.template_id);
    const hasExistingProject = !isEmpty(existingProjects);

    if (hasExistingProject && !skipCheck) {
      setSelectedTemplate(template);
      setShowModal(true);

      return;
    }

    setIsCreatingProject(true);

    try {
      const createProjectCallback = async (project) => {
        const DSInstance = getDSInstance();

        let subCategory = foundSecondaryCategory()?.find((cat) => cat.id === selectedSecondaryCategory);

        if (!subCategory)
          subCategory = foundSecondaryCategory()?.find((cat) => String(cat?.name).toLowerCase() === selectedColor);

        await DSInstance.post('/template-builder/project', {
          templateId: template.template_id,
          projectId: project.project_id,
          customizationHash: userCustomizationsHash,
          categoryId: currentCategory?.id,
          subCategoryId: selectedSecondaryCategory,
          createdFrom: {
            category: currentCategory,
            subCategory,
          },
          metadata: { template, project },
        });

        setIsCreatingProject(false);

        window.open(`/templates/project/${project.project_id}`, '_blank');
      };

      createProject(template, createProjectCallback);
    } catch (error) {
      console.error('error', error);
    }
  };

  const handlePreview = (template) => {
    if (template.smartThumbnailUrl) {
      setPreviewTemplate(template);
      setShowPreviewModal(true);
    }
  };

  const handleModalConfirm = () => {
    setShowModal(false);

    handleCustomize(selectedTemplate, true);

    // if (selectedTemplate) {
    //   window.open(`//templates/project/${project.projectId}`, '_blank');
    // }
    // setSelectedTemplate(null);
  };

  const goToProjectsText = 'Open Existing Project';

  const handleModalCancel = (e) => {
    const existingProjects = existingProjectsWith(selectedTemplate?.template_id);

    setShowModal(false);
    setSelectedTemplate(null);

    if (e.target?.textContent === goToProjectsText) {
      if (existingProjects?.length === 1 && existingProjects[0]?.project_id) {
        history.push(`/templates/project/${existingProjects[0].project_id}`);
      } else {
        history.push('/dashboard/templates/my-projects');
      }
    }
  };

  const handleSecondaryCategoryChange = (categoryId) => {
    setSelectedSecondaryCategory(categoryId);
    if (categoryId !== 'all') {
      handleSelectSecondaryCategory(currentCategory, categoryId);
    }
  };

  useEffect(() => {
    // Reset filters first, then set new category
    resetSelectedFilters();

    const categoryFromUrl = getCategoryFromUrl();
    setCurrentCategory(categoryFromUrl);

    setSelectedColor('blue');

    // setSelectedSecondaryCategory('all'); // Reset to 'all' when route changes
    setHasInitialized(false); // Reset initialization when route changes
  }, [location.pathname, resetSelectedFilters]);

  useEffect(() => {
    const secondaryCategory = foundSecondaryCategory()[0]?.id;

    // Only execute when both currentCategory and searchCategories are available, and haven't initialized yet
    if (currentCategory && searchCategories?.secondary && secondaryCategory && !hasInitialized) {
      resetSelectedFilters(true);

      if (isAtColorPage()) {
        toggleAllSecondaryCategories(currentCategory);
      } else {
        handleSecondaryCategoryChange(secondaryCategory);
      }

      setHasInitialized(true);
    }
  }, [currentCategory, searchCategories, hasInitialized, foundSecondaryCategory, handleSelectSecondaryCategory]);

  // useEffect(() => {
  //   if (!navbarCategories && !isEmpty(dhTemplates)) {
  //     const pluckedCategories = map(dhTemplates, 'primary_template_category_item');
  //     const uniqCategories = uniqWith(pluckedCategories, isEqual);

  //     const categoryAll = {
  //       template_category_item_id: 0,
  //       item_name: 'All',
  //     };

  //     setNavbarCategories([categoryAll, ...uniqCategories]);
  //   }
  // }, [dhTemplates]);

  if (isCreatingProject) {
    return <Loader title="Creating Project" height="50vh" />;
  }

  if (isLoadingTemplates) {
    return <RelativeLoading isLoading={true} />;
  }

  // Render the appropriate component based on route
  const renderTemplateComponent = () => {
    if (isSocialMediaRoute()) {
      return (
        <SocialMediaTemplates
          filteredTemplates={filteredTemplates}
          selectedSecondaryCategory={selectedSecondaryCategory}
          foundSecondaryCategory={foundSecondaryCategory}
          handleSelectSecondaryCategory={handleSecondaryCategoryChange}
          toggleAllSecondaryCategories={toggleAllSecondaryCategories}
          resetSelectedFilters={resetSelectedFilters}
          currentCategory={currentCategory}
          onCustomize={handleCustomize}
          onPreview={handlePreview}
        />
      );
    }

    if (isEmailBannerRoute()) {
      return (
        <EmailTemplates
          filteredTemplates={filteredTemplates}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          selectedSecondaryCategory={selectedSecondaryCategory}
          foundSecondaryCategory={foundSecondaryCategory}
          handleSelectSecondaryCategory={handleSecondaryCategoryChange}
          resetSelectedFilters={resetSelectedFilters}
          onCustomize={handleCustomize}
          onPreview={handlePreview}
        />
      );
    }

    if (isAtColorPage()) {
      const colorsToHide = [];

      if (isWelcomeBundleRoute()) colorsToHide.push('green');
      if (isAdsRoute()) colorsToHide.push('gray');

      return (
        <AdsTemplates
          filteredTemplates={filteredTemplates}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
          onCustomize={handleCustomize}
          onPreview={handlePreview}
          colorsToHide={colorsToHide}
        />
      );
    }

    // Default template component for all other routes
    return (
      <DefaultTemplates
        filteredTemplates={filteredTemplates}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedSecondaryCategory={selectedSecondaryCategory}
        foundSecondaryCategory={foundSecondaryCategory}
        handleSelectSecondaryCategory={handleSecondaryCategoryChange}
        toggleAllSecondaryCategories={toggleAllSecondaryCategories}
        resetSelectedFilters={resetSelectedFilters}
        currentCategory={currentCategory}
        onCustomize={handleCustomize}
        onPreview={handlePreview}
      />
    );
  };

  return (
    <div className="w-full">
      {renderTemplateComponent()}

      <ConfirmModal
        isOpen={showModal}
        title="You've already created a project with this template."
        text="Looks like you've already used this template to create a project. Do you want to start a new one or go back to the existing project?"
        confirmLabel="Start New Project"
        cancelLabel={goToProjectsText}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        cancelButtonProps={{}}
        width="600px"
        hasCloseButton
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewTemplate}
        onCustomize={handleCustomize}
      />
    </div>
  );
};

const TemplatesPage = (props) => {
  const getPageSubtitle = () => {
    if (isInstantTrustPackageRoute()) {
      return 'Use the Instant Trust Package templates to quickly customize and send a professional first impression to your leads.';
    }

    if (isSocialMediaRoute()) {
      return 'Use the monthly calendar to save time—just customize the templates and start posting.';
    }

    if (isWelcomeBundleRoute()) {
      return 'Use the welcome bundle to make a strong first impression and give new leads everything they need to remember you.';
    }

    if (isAdsRoute()) {
      return 'Use custom ads to promote your expertise, showcase your book, and drive consistent visibility online.';
    }

    if (isEmailBannerRoute()) {
      return 'Use an email banner to create recognition and ensure every email reinforces your personal brand.';
    }

    return '';
  };

  return (
    <DesignHuddleProvider>
      <DashboardTemplate
        title={getCategoryFromUrl()?.name}
        titleClass="text-2xl font-bold flex-1 mb-2"
        subHeader={(
          <div className="mb-8 leading-5 font-medium">{getPageSubtitle()}</div>
        )}
        centralized
        {...props}
      >
        <TemplatesContent />
      </DashboardTemplate>
    </DesignHuddleProvider>
  );
};

export default compose(withProfile, withAuth)(TemplatesPage);